import { useCallback, useEffect, useState } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, imgUrl } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { AdminHeading } from "@/pages/admin/AdminShell";
import { ProductForm, emptyProduct } from "@/pages/admin/ProductForm";
import { Mark } from "@/components/Logo";

export default function AdminProducts() {
  const { t, tr, fmt } = useLang();
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/products").then((r) => setList(Array.isArray(r.data) ? r.data : [])).catch(() => setList([]));
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  const del = async (p) => {
    if (!window.confirm(`${t("a_confirm_delete")}\n${tr(p.name)}`)) return;
    try {
      await api.delete(`/admin/products/${p.id}`);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const edit = (p) => setEditing({ ...emptyProduct(), ...p, compare_price: p.compare_price ?? "" });

  return (
    <div data-testid="admin-products">
      <AdminHeading title={t("a_products")}>
        <button data-testid="admin-add-product-btn" onClick={() => setEditing(emptyProduct())} className="flex items-center gap-2 bg-[#0B0B0C] px-5 py-3 text-sm text-[#F7F3EB] hover:bg-[#241914]">
          <Plus className="h-4 w-4" /> {t("a_add_product")}
        </button>
      </AdminHeading>
      {list.length === 0 ? (
        <p className="py-16 text-center text-[#241914]/70" data-testid="admin-products-empty">{t("a_no_products")}</p>
      ) : (
        <div className="divide-y divide-[#241914]/10 border-y border-[#241914]/10">
          {list.map((p) => (
            <div key={p.id} className="flex items-center gap-4 py-4" data-testid={`admin-product-row-${p.id}`}>
              <div className="h-16 w-12 shrink-0 overflow-hidden bg-[#E8DFD0]">{p.images?.[0] ? <img src={imgUrl(p.images[0])} alt="" className="h-full w-full object-cover" /> : <Mark className="m-auto mt-4 h-7 w-6 text-[#C6A76A]" />}</div>
              <div className="min-w-0 flex-1">
                <p className="font-display truncate text-xl">{tr(p.name)}</p>
                <p className="text-xs text-[#241914]/65">
                  {p.volume} {!p.active && <span className="ms-2 bg-[#241914]/10 px-2 py-0.5">{t("a_hidden")}</span>} {p.featured && <span className="ms-2 bg-[#C6A76A]/25 px-2 py-0.5">{t("a_featured")}</span>}
                </p>
              </div>
              <div className="hidden w-32 text-end sm:block"><p className="text-sm">{fmt(p.price)}</p></div>
              <div className="w-20 text-end">
                <p className={`text-sm ${p.stock <= 3 ? "text-[#9b2c2c]" : ""}`} data-testid={`admin-product-stock-${p.id}`}>{p.stock}</p>
                <p className="text-[0.65rem] text-[#241914]/55">{t("a_stock")}</p>
              </div>
              <button data-testid={`admin-product-edit-${p.id}`} onClick={() => edit(p)} className="p-2 hover:text-[#8a6d35]" aria-label={t("a_edit")}><Pencil className="h-4 w-4" strokeWidth={1.3} /></button>
              <button data-testid={`admin-product-delete-${p.id}`} onClick={() => del(p)} className="p-2 hover:text-[#9b2c2c]" aria-label={t("a_delete")}><Trash2 className="h-4 w-4" strokeWidth={1.3} /></button>
            </div>
          ))}
        </div>
      )}
      {editing && <ProductForm initial={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}
